/* eslint-disable import/default */
import React from 'react'
import { render } from 'react-dom'
import './polyfills'
import { ConnectedRouter } from 'connected-react-router'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { createBrowserHistory } from 'history'
import { CookiesProvider } from 'react-cookie'
import {
  LocaleListener,
  MerchantPortalBase,
  useLocale,
} from '@merchant-portal/merchant-portal-base'
import * as Sentry from '@sentry/react'
import daLocale from 'moment/locale/da'
import fiLocale from 'moment/locale/fi'
import nbLocale from 'moment/locale/nb'
import svLocale from 'moment/locale/sv'
import moment from 'moment/moment'
import { init as initApm } from '@elastic/apm-rum'
import configureStore from './client/app/store/configure-store'
import App from './client/app/App'
import { translator } from './client/app/internal/TextLabels/text'
import { configureSentryRedux } from './client/app/sentry/initializeReduxForSentry'
import SentryErrorBoundary from './client/app/sentry/SentryErrorBoundary'
import config from './config'
import { Languages } from './client/app/models/languages'
import { sanitizeSentryEventData } from './client/app/sentry/sanitizeSentryEventData'
import { ModuleInfoProvider } from './client/modules/provider/ModuleInfoProvider'

import './base.scss'

const history = createBrowserHistory()

if (['dev', 'integration', 'prod'].includes(config.apmEnvironment)) {
  initApm({
    serviceName: 'merchant-portal',
    serverUrl: config.apmServiceUrl,
    environment: config.apmEnvironment,
    logLevel: 'warn',
  })
}

if (
  config.sentryEnabled &&
  Array.of('dev', 'integration', 'production').includes(
    config.sentryEnvironment,
  )
) {
  const SENTRY_RELEASE_VERSION = process.env.REACT_APP_GIT_COMMIT
  Sentry.init({
    dsn: 'https://02365cd909847791c40fe6c006a1d9b4@o566388.ingest.us.sentry.io/4506930987270144',
    integrations: [Sentry.browserTracingIntegration()],
    normalizeDepth: 10,
    environment: config.sentryEnvironment,
    release: `merchant-portal@${SENTRY_RELEASE_VERSION}`,
    beforeSend: event => {
      const skipSanitize = event?.extra?.skipSanitize
      if (skipSanitize) {
        // eslint-disable-next-line no-console
        console.log(event)
        return event
      }
      const sanitizedSentryEventData = sanitizeSentryEventData(event)
      // eslint-disable-next-line no-console
      console.log(sanitizedSentryEventData)

      return sanitizedSentryEventData
    },
  })
}

const sentryReduxEnhancer = configureSentryRedux()
const { store, persistor } = configureStore(history, sentryReduxEnhancer)
window.sessionStorage.setItem('search', window.location.search)

const MainApp = () => {
  const [{ language }] = useLocale()
  moment.locale(language || Languages.SWEDISH, [
    daLocale,
    fiLocale,
    nbLocale,
    svLocale,
  ])

  if (window && window.Cypress) {
    window.store = store
  }

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ConnectedRouter history={history}>
          <CookiesProvider>
            <MerchantPortalBase>
              <LocaleListener translators={[translator]}>
                <SentryErrorBoundary>
                  <ModuleInfoProvider>
                    <App />
                  </ModuleInfoProvider>
                </SentryErrorBoundary>
              </LocaleListener>
            </MerchantPortalBase>
          </CookiesProvider>
        </ConnectedRouter>
      </PersistGate>
    </Provider>
  )
}

export default MainApp
render(<MainApp />, document.getElementById('app'))
